import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "The mistake that strangles engineering teams",
  "description": "You work hard on a feature. Weeks of back and forth with designers, product managers, tech leads, and the rest of your team. Your feature is amazing and you're proud as heck! But then ...",
  "published": "2020-10-26T00:00:00.000Z",
  "hero": "./img/screenshot-1603726194318.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Does this sound familiar?`}</p>
    <p>{`You work hard on a feature. Weeks of back and forth with designers, product managers, tech leads, and the rest of your team. Your feature is amazing and you're proud as heck!`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media0.giphy.com/media/5mTwWeN3MoS08/giphy-loop.mp4?cid=7cf28b0f081024dmt8zebt7k0si10i44i31siwhj7y8gcvr8&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Time to show off. Make it live on the staging environment, ask the QA team to play around, tell the PM to verify, show it off to the sales team ... Everyone must know! 🤘`}</p>
    <p><em parentName="p">{`"Sorry the Thingamabob Team is using staging this week, they're on a critical deadline and can't afford any interruptions"`}</em></p>
    <p>{`No staging for you.`}</p>
    <p>{`Okay fine. You set up a demo with the PM and run them through the feature using localhost. They can't try it themselves but better than nothing.`}</p>
    <p>{`QA and sales will have to wait. It's not going out this week anyway.`}</p>
    <p>{`Next week rolls around and you're ready to show off. `}<em parentName="p">{`"Couple more days"`}</em>{` says the Thingamabob Team. Running behind, found a bug.`}</p>
    <p>{`By Wednesday, staging is ready for you. Wide and open. All it needs is your wonderful code.`}</p>
    <p>{`Oh wait no, critical production bug. Gonna need staging to verify before shipping because the fix might break a payment form.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/woqoXtjs9E8MYfenLW/giphy-loop.mp4?cid=7cf28b0f5zq7i0hvspdkzf0pshb5cdpd7rmi0v5uo76tmwpr&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h2 {...{
      "id": "how-engineering-teams-get-here"
    }}>{`How engineering teams get here`}</h2>
    <p>{`I've seen this at every growing company my friend. You're not alone.`}</p>
    <p>{`You start as a small team. A scrappy band of miscreants building the future. And you ship straight to production 🤘`}</p>
    <p>{`Then someone causes a problem. Production goes down, a sale is lots, a customer is pissed.`}</p>
    <p>{`Time for process.`}</p>
    <p>{`You'll need a development environment. A place where everyone can push their reviewed code and see how it works in a live environment. Integrated with the backend, hosted on servers, static files on CDN, the works.`}</p>
    <p>{`Development changes a lot. Hourly sometimes. Minute by minute even. You never know what you're gonna get.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/IgGLggVL4HXYDAot0Y/giphy-loop.mp4?cid=7cf28b0fasoj5sg5c0hbz9bz54gxpt180ay04r76qa0uhe5y&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`You'll need a staging environment. Like a more stable development where you can test.`}</p>
    <p>{`QA can trust the system won't fall apart under their feet. Product managers know engineers tested these features and are certain it all works.`}</p>
    <p>{`Not production, but ready to show off.`}</p>
    <p>{`Production is production. You don't touch that unless you're certain the code won't break.`}</p>
    <p>{`You ship, but carefully.`}</p>
    <p>{`Production falls weeks or months behind development and staging. Long projects, gotta make sure they're perfect. 🤞`}</p>
    <h2 {...{
      "id": "and-then-the-team-starts-to-grow"
    }}>{`And then the team starts to grow`}</h2>
    <p>{`The production-staging-development split works great. You have a solid product, happy users, and everyone knows you as a reliable team that doesn't break things.`}</p>
    <p>{`Not like those other companies who move fast and break things. Ew.`}</p>
    <p>{`With your success the team grows. What used to be 5 folks becomes 10, 15, 30, 50 ... wow 😍`}</p>
    <p>{`But 10 is where trouble starts.`}</p>
    <p>{`You work on 2 or 3 projects, maybe 4, not just 1. You have different goals. You're moving in separate directions.`}</p>
    <p>{`The frontend team wants to ship fast and test with users. The backend team is swamped with API changes and database schemas. The infrastructure team ... who knows what the infrastructure team does.`}</p>
    <p>{`You start stepping on each other's toes.`}</p>
    <p>{`You can't test while Thingamabob is testing. They can't test while you're testing. Who knows what might break.`}</p>
    <p>{`You plead with the infrastructure team to give you more environments. This is ridiculous how can you ship anything on time if you keep getting stuck waiting in line?`}</p>
    <p><em parentName="p">{`"Sorry"`}</em>{`, says infra, `}<em parentName="p">{`"we can't. Took us 2 weeks to create these environments, we don't have time to make more"`}</em></p>
    <p>{`You sigh and get to work on the next project. At least you can work on the future while the present waits to ship.`}</p>
    <p>{`Crap! You just used a method Bob from the Thingamajigger project that Bob merged to develop. Now you gotta wait for that whole project to be ready before you can ship.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/fXK6nNrqoW9NNWWcPD/giphy-loop.mp4?cid=7cf28b0f7m88vjcoji2d8f1vx9aapqm9ex80m9r359rvd6fj&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Sound familiar? Hit reply`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: tomorrow I'll show you a way out of this mess I've seen work in the past`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      